/**
 * Shape of a single entry stored in Firebase under
 * `envs/{env}/allowlists/wifi/allowedCountries`.
 *
 * NOTE: the naming is misleading — `countryName` holds the ISO 3166-1 alpha-2 code
 * (e.g. "AE"), which is what the app needs, while `countryCode` is the phone prefix
 * (e.g. "971") and is ignored here.
 */
interface FirebaseCountryEntry {
  countryCode?: unknown;
  countryName?: unknown;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isCountryEntryObject = (value: unknown): value is FirebaseCountryEntry =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && 'countryName' in value;

/** A value that represents a single country: either an ISO code string or a `{ countryName }` object. */
const isCountryValue = (value: unknown): boolean =>
  typeof value === 'string' || isCountryEntryObject(value);

const toIsoCode = (value: unknown): string | null => {
  if (isNonEmptyString(value)) return value;
  if (isCountryEntryObject(value) && isNonEmptyString(value.countryName)) return value.countryName;
  return null;
};

const toIsoCodes = (values: unknown[]): string[] =>
  values.map(toIsoCode).filter((code): code is string => code !== null);

/**
 * Normalizes the Firebase `allowedCountries` node into a list of ISO 3166-1 alpha-2 codes.
 *
 * Supported shapes:
 * - `[{ countryCode: "971", countryName: "AE" }, ...]` — the actual shape stored in Firebase
 * - `["AE", "BH"]` — plain array of ISO codes
 * - `{ "0": "AE", "1": { countryName: "BH" } }` — array returned as an object (sparse Firebase arrays)
 * - `{ AE: true, BH: { enabled: true } }` — map keyed by ISO code
 */
export function parseFirebaseCountries(data: unknown): string[] {
  if (data === null || data === undefined) return [];
  if (typeof data === 'object' && !Array.isArray(data) && 'error' in data) {
    throw new Error(`Firebase error: ${JSON.stringify((data as Record<string, unknown>).error)}`);
  }
  if (Array.isArray(data)) return toIsoCodes(data);
  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const values = Object.values(record);
    return values.every(isCountryValue) ? toIsoCodes(values) : Object.keys(record).filter(Boolean);
  }

  return [];
}
